import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import SearchSelect, { SearchSelectItem } from '../atoms/SearchSelect'
import { Client, FETCH_CLIENTS, SET_CURRENT_PAGE, SET_SEARCH_TERM } from 'src/redux/clientsReducer'
import { RootReducer } from 'src/redux/rootReducer'

interface ClientSearchSelectProps {
  selectedId: string
  selectedLabel: string
  placeholder: string
  error?: boolean
  onSelect: (client: Client) => void
  onClear: () => void
}

const clientSubtitle = (client: Client): string => {
  return [client.email, client.phone_number].filter((part) => part.length > 0).join(' · ')
}

const ClientSearchSelect = ({
  selectedId,
  selectedLabel,
  placeholder,
  error = false,
  onSelect,
  onClear
}: ClientSearchSelectProps): JSX.Element => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const clients = useSelector((state: RootReducer) => state.clientsReducer.clients)
  const loading = useSelector((state: RootReducer) => state.clientsReducer.loading)
  const items: SearchSelectItem[] = clients.map((client) => ({
    id: client.id,
    title: client.name,
    subtitle: clientSubtitle(client)
  }))

  const handleOpen = useCallback(() => {
    dispatch(SET_CURRENT_PAGE(1))
    dispatch(SET_SEARCH_TERM(''))
    dispatch(FETCH_CLIENTS())
  }, [dispatch])

  const handleClose = useCallback(() => {
    dispatch(SET_SEARCH_TERM(''))
  }, [dispatch])

  const handleSearchChange = useCallback(
    (value: string) => {
      dispatch(SET_SEARCH_TERM(value))
      dispatch(SET_CURRENT_PAGE(1))
      dispatch(FETCH_CLIENTS())
    },
    [dispatch]
  )

  const handleSelect = (item: SearchSelectItem): void => {
    const client = clients.find((entry) => entry.id === item.id) ?? null
    if (client !== null) {
      onSelect(client)
    }
  }

  return (
    <SearchSelect
      selectedId={selectedId}
      selectedLabel={selectedLabel}
      placeholder={placeholder}
      searchPlaceholder={t('sell_contract.search_clients')}
      loadingLabel={t('my_vehicles.loading_offers')}
      emptyLabel={t('sell_contract.no_clients')}
      error={error}
      items={items}
      loading={loading}
      onOpen={handleOpen}
      onClose={handleClose}
      onSearchChange={handleSearchChange}
      onSelect={handleSelect}
      onClear={onClear}
    />
  )
}

export default ClientSearchSelect
